import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'wouter';
import { cn } from '@/lib/utils';
import { useAccess } from '@/hooks/use-access';
import { usePrivacyMode } from '@/hooks/use-privacy-mode';
import { tenantConfig } from '@/lib/sovereign';

type FeatureKey = keyof typeof tenantConfig.features;

interface NavItem {
  href: string;
  label: string;
  icon: React.ReactNode;
  premium?: boolean;
  featureGate?: FeatureKey;
}

interface NavSection {
  title?: string;
  items: NavItem[];
}

const I = {
  overview: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
    </svg>
  ),
  ai: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a5 5 0 015 5v1a2 2 0 012 2v1a2 2 0 01-2 2H7a2 2 0 01-2-2v-1a2 2 0 012-2V7a5 5 0 015-5z" />
      <path d="M9 22v-4a3 3 0 016 0v4" />
      <circle cx="12" cy="7" r="1" fill="currentColor" />
    </svg>
  ),
  protocol: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
    </svg>
  ),
  projections: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  ),
  history: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  ),
  activity: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" />
    </svg>
  ),
  team: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
    </svg>
  ),
  rewards: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
  analytics: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
    </svg>
  ),
  yield: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  ),
  marketing: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  ),
  growth: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="18" rx="2" /><path d="M8 10h8" /><path d="M12 6v8" />
    </svg>
  ),
  planner: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  spiderweb: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><path d="M12 2v20" /><path d="M2 12h20" /><path d="M4.93 4.93l14.14 14.14" /><path d="M19.07 4.93L4.93 19.07" />
    </svg>
  ),
};

const SIDEBAR_NAV: NavSection[] = [
  {
    items: [
      { href: '/dashboard', label: 'Overview', icon: I.overview },
      { href: '/dashboard/ai', label: 'Origin AI', icon: I.ai, premium: true, featureGate: 'aiAdvisor' },
      { href: '/dashboard/protocol', label: 'Protocol', icon: I.protocol, featureGate: 'protocolOverview' },
    ],
  },
  {
    title: 'Portfolio',
    items: [
      { href: '/dashboard/projections', label: 'Projections', icon: I.projections, featureGate: 'projections' },
      { href: '/dashboard/history', label: 'Balance History', icon: I.history, premium: true, featureGate: 'balanceHistory' },
      { href: '/dashboard/activity', label: 'Activity', icon: I.activity, premium: true, featureGate: 'activity' },
    ],
  },
  {
    title: 'Earn',
    items: [
      { href: '/dashboard/team', label: 'Team Dashboard', icon: I.team, premium: true, featureGate: 'spiderWeb' },
      { href: '/dashboard/rewards', label: 'Rewards Hub', icon: I.rewards, premium: true, featureGate: 'rewardsHub' },
    ],
  },
  {
    title: 'Calculators',
    items: [
      { href: '/dashboard/calculator', label: 'Growth Projector', icon: I.growth, featureGate: 'calculator' },
      { href: '/dashboard/calculator/yield', label: 'Yield Planner', icon: I.planner, featureGate: 'calculator' },
      { href: '/dashboard/calculator/spiderweb', label: 'Spider Web', icon: I.spiderweb, featureGate: 'calculator' },
    ],
  },
  {
    title: 'Tools',
    items: [
      { href: '/dashboard/analytics', label: 'Analytics', icon: I.analytics, premium: true, featureGate: 'analytics' },
      { href: '/dashboard/yield', label: 'Yield Tools', icon: I.yield, premium: true, featureGate: 'yieldTools' },
      { href: '/dashboard/marketing', label: 'Marketing', icon: I.marketing, premium: true, featureGate: 'marketingMaterials' },
    ],
  },
];

// Map a nav item to its Anubis migration phase (only Team Dashboard /
// spiderWeb and Rewards Hub / rewardsHub are migrating; everything else,
// including the Spider Web *calculator*, stays 'active').
function migrationPhaseForItem(item: NavItem): 'active' | 'migrating' | 'moved' {
  const m = tenantConfig.anubisMigration;
  if (!m) return 'active';
  if (item.featureGate === 'spiderWeb') return m.spiderWebPhase;
  if (item.featureGate === 'rewardsHub') return m.rewardsHubPhase;
  return 'active';
}

function isVisible(item: NavItem): boolean {
  if (migrationPhaseForItem(item) === 'moved') return false; // feature has left for Anubis
  if (!item.featureGate) return true;
  return tenantConfig.features[item.featureGate] !== false;
}

export function PrivacyToggle() {
  const { isHidden, toggle } = usePrivacyMode();
  return (
    <button
      onClick={toggle}
      className={cn(
        'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all border shrink-0',
        isHidden
          ? 'bg-amber-400/10 text-amber-400 border-amber-400/25 hover:bg-amber-400/20'
          : 'text-slate-400 hover:text-white hover:bg-zinc-800/50 border-transparent'
      )}
      title={isHidden ? 'Show balances' : 'Hide balances'}
      aria-label={isHidden ? 'Show balances' : 'Hide balances'}
    >
      {isHidden ? (
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
        </svg>
      ) : (
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      )}
      <span className="hidden sm:inline">{isHidden ? 'Hidden' : 'Visible'}</span>
    </button>
  );
}

function SidebarNavItem({ item, location, isPremium, onClick }: { item: NavItem; location: string; isPremium: boolean; onClick?: () => void }) {
  const isActive = location === item.href;
  const locked = item.premium && !isPremium;
  const migrating = migrationPhaseForItem(item) === 'migrating';

  return (
    <Link
      href={item.href}
      onClick={onClick}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-all',
        isActive
          ? 'bg-brand/10 text-brand border border-brand/25'
          : locked
            ? 'text-zinc-500 hover:text-zinc-400 border border-transparent'
            : 'text-slate-400 hover:text-white hover:bg-zinc-800/50 border border-transparent'
      )}
    >
      <span className="shrink-0">{item.icon}</span>
      <span className="flex-1">{item.label}</span>
      {migrating && (
        <span className="shrink-0 text-[9px] font-bold uppercase tracking-wide text-amber-400 bg-amber-400/10 border border-amber-400/25 rounded px-1 py-0.5">
          Anubis
        </span>
      )}
      {locked && (
        <svg className="w-3 h-3 shrink-0 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      )}
    </Link>
  );
}

function DesktopSidebar({ location, isPremium }: { location: string; isPremium: boolean }) {
  return (
    <aside className="hidden lg:flex w-56 flex-col shrink-0 border-r border-zinc-800 bg-zinc-950 h-[calc(100vh-4rem)] sticky top-16 overflow-y-auto">
      <nav className="flex-1 py-3 px-3 space-y-4" aria-label="Dashboard sidebar">
        {SIDEBAR_NAV.map((section, si) => {
          const visibleItems = section.items.filter(isVisible);
          if (visibleItems.length === 0) return null;
          return (
            <div key={si}>
              {section.title && (
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider px-2.5 mb-1.5">{section.title}</p>
              )}
              <div className="space-y-0.5">
                {visibleItems.map((item) => (
                  <SidebarNavItem key={item.href} item={item} location={location} isPremium={isPremium} />
                ))}
              </div>
            </div>
          );
        })}
      </nav>
    </aside>
  );
}

function MobileDrawer({ open, onClose, location, isPremium }: { open: boolean; onClose: () => void; location: string; isPremium: boolean }) {
  useEffect(() => {
    if (!open) {
      return;
    }
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={onClose} />
      <aside className="fixed inset-y-0 left-0 w-64 bg-zinc-950 border-r border-zinc-800 z-50 lg:hidden flex flex-col animate-in slide-in-from-left duration-200">
        <div className="h-14 flex items-center justify-between px-4 border-b border-zinc-800">
          <span className="text-sm font-semibold text-white">Dashboard</span>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-zinc-800 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <nav className="flex-1 py-3 px-3 space-y-4 overflow-y-auto" aria-label="Dashboard mobile navigation">
          {SIDEBAR_NAV.map((section, si) => {
            const visibleItems = section.items.filter(isVisible);
            if (visibleItems.length === 0) return null;
            return (
              <div key={si}>
                {section.title && (
                  <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider px-2.5 mb-1.5">{section.title}</p>
                )}
                <div className="space-y-0.5">
                  {visibleItems.map((item) => (
                    <SidebarNavItem key={item.href} item={item} location={location} isPremium={isPremium} onClick={onClose} />
                  ))}
                </div>
              </div>
            );
          })}
        </nav>
      </aside>
    </>
  );
}

export function DashboardSidebar({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const { isPremium } = useAccess();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location]);

  return (
    <div className="flex min-h-[calc(100vh-4rem)]">
      <DesktopSidebar location={location} isPremium={isPremium} />
      <MobileDrawer open={drawerOpen} onClose={closeDrawer} location={location} isPremium={isPremium} />
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="lg:hidden sticky top-16 z-30 bg-zinc-950/95 backdrop-blur-sm border-b border-zinc-800/60 px-4 py-2">
          <button
            onClick={() => setDrawerOpen(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium text-slate-400 hover:text-white hover:bg-zinc-800/50 border border-zinc-800 transition-all"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            Menu
          </button>
        </div>
        <div className="flex-1">
          {children}
        </div>
      </div>
    </div>
  );
}

export function DashboardSubNav() {
  return null;
}
